/**
 * Which businesses ("firms") a member of staff may act for, and switching the
 * one they act in (ORDR-184). Reference: paio-order-manager
 * `src/business/firmApi.ts` (ORDR-155, ORDR-184 part 1).
 *
 * paio-auth's self-service views, through the gateway:
 *
 * - `GET /auth/me/memberships`: `{memberships: [{firm_id, firm_name,
 *   tenant_slug, role_slug, is_active}], active_firm_id}`. `active_firm_id` may
 *   name a REACHED firm, which is not among the memberships. The list is EVERY
 *   firm the person belongs to, in any app.
 * - `GET /auth/me/reach` with `X-Tenant-ID: ordering`: `{firms: [{firm_id,
 *   name, app, scope_type, role_key, via}]}`, the firms reached by GRANT. A
 *   reseller-admin is a member of the reseller only and reaches each of its
 *   restaurants this way. Reach in any app but ordering is dropped.
 * - `POST /auth/me/active-firm` `{firm_id}` accepts a membership or a reached
 *   firm; the gateway then stamps it as `X-Firm-Id`.
 *
 * ORDERING BUSINESSES ONLY (ORDR-207). Measured in production on 2026-10-16:
 * a platform operator was offered firms of the senior-living (`horizon`),
 * leftBrain (`leftbrain`) and platform (`paio`) tenancies beside the
 * restaurants, none of which has anything in ordering. A membership is kept
 * only when its firm's `tenant_slug` is the ordering tenancy, the same
 * constant reach is filtered by. A row with no tenancy is dropped: it cannot
 * be an ordering business. `active_firm_id` is returned as the server named
 * it, so the switcher can see an active firm it does not list.
 *
 * Every read is a RESULT with its correlation id, never a throw and never an
 * empty list standing in for "we could not ask".
 */

import type { ApiClient, ApiResponse } from "../api/client.js";

/**
 * The tenancy `/me/reach` is filtered by, the only app whose reach is a
 * restaurant, and the only tenancy whose memberships are listed (ORDR-207).
 */
const ORDERING = "ordering";

/**
 * Sent on every call here (ORDR-208). Measured in production on 2026-10-16:
 * a memberships read without it is made in the tenancy the gateway derives
 * (`paio`), paio-auth refuses the stored ordering choice for the mismatch, and
 * the switcher reverts to "Choose a business" after every switch.
 */
const IN_ORDERING = { "X-Tenant-ID": ORDERING } as const;

export interface Business {
  readonly firmId: string;
  readonly name: string;
  readonly roleLabel: string;
}

export type BusinessesResult =
  | { readonly kind: "ready"; readonly activeFirmId: string | null; readonly businesses: readonly Business[] }
  | { readonly kind: "failed"; readonly correlationId: string };

export type ReachResult =
  | { readonly kind: "ready"; readonly businesses: readonly Business[] }
  | { readonly kind: "failed"; readonly correlationId: string };

export type SwitchResult = { readonly kind: "switched" } | { readonly kind: "failed"; readonly correlationId: string };

type WireRow = Record<string, unknown> | null | undefined;

function dataOf(response: ApiResponse): WireRow {
  return response.status === 200 ? ((response.body as WireRow)?.["data"] as WireRow) : null;
}

function business(firmId: unknown, name: unknown, role: unknown): Business | null {
  if (typeof firmId !== "string" || firmId === "") {
    return null;
  }
  return {
    firmId: firmId.toLowerCase(),
    name: typeof name === "string" && name !== "" ? name : "Unnamed business",
    roleLabel: typeof role === "string" ? role : "",
  };
}

export async function loadMemberships(client: ApiClient): Promise<BusinessesResult> {
  // ORDR-208: in the ordering tenancy, or the stored choice is refused and the
  // platform firm comes back active.
  const response = await client.request("/auth/me/memberships", { headers: IN_ORDERING });
  const data = dataOf(response);
  const rows = data?.["memberships"];
  if (!Array.isArray(rows)) {
    return { kind: "failed", correlationId: response.correlationId };
  }
  const businesses: Business[] = [];
  for (const raw of rows as WireRow[]) {
    const held =
      raw?.["is_active"] === true && raw["tenant_slug"] === ORDERING
        ? business(raw["firm_id"], raw["firm_name"], raw["role_slug"])
        : null;
    if (held !== null) {
      businesses.push(held);
    }
  }
  const active = data?.["active_firm_id"];
  return { kind: "ready", activeFirmId: typeof active === "string" ? active.toLowerCase() : null, businesses };
}

export async function loadReach(client: ApiClient): Promise<ReachResult> {
  // Reach is filtered to the request's tenancy (ORDR-184, ORDR-208).
  const response = await client.request("/auth/me/reach", { headers: IN_ORDERING });
  const firms = dataOf(response)?.["firms"];
  if (!Array.isArray(firms)) {
    return { kind: "failed", correlationId: response.correlationId };
  }
  const businesses: Business[] = [];
  for (const raw of firms as WireRow[]) {
    const reached = raw?.["app"] === ORDERING ? business(raw["firm_id"], raw["name"], raw["role_key"]) : null;
    if (reached !== null) {
      businesses.push(reached);
    }
  }
  return { kind: "ready", businesses };
}

/**
 * The memberships, then each reached firm not already one (a membership
 * wins). Either read failing is a failure: a list missing its restaurants
 * would hide a kitchen the person does run.
 */
export function withReach(memberships: BusinessesResult, reach: ReachResult): BusinessesResult {
  if (memberships.kind === "failed") {
    return memberships;
  }
  if (reach.kind === "failed") {
    return reach;
  }
  const held = new Set(memberships.businesses.map((entry) => entry.firmId));
  return {
    ...memberships,
    businesses: [...memberships.businesses, ...reach.businesses.filter((entry) => !held.has(entry.firmId))],
  };
}

export async function switchFirm(client: ApiClient, firmId: string): Promise<SwitchResult> {
  // ORDR-208: the same tenancy as the read that must return the choice. At
  // paio-auth f0984626 the POST does not read the header (it accepts any firm
  // held or reached), so it changes nothing there today; it keeps the write
  // and its read-back in one tenancy if the POST is ever narrowed.
  const response = await client.request("/auth/me/active-firm", {
    method: "POST",
    json: { firm_id: firmId },
    headers: IN_ORDERING,
  });
  return response.status !== null && response.status >= 200 && response.status < 300
    ? { kind: "switched" }
    : { kind: "failed", correlationId: response.correlationId };
}
